import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Link, Avatar, Box, Button, Divider, Typography } from "@mui/material";

import {
  AndroidIcon,
  AppleIcon,
  VerifiedIcon,
  DashboardIcon,
  FeedIcon,
  GoServiceIcon,
  ExploreIcon,
  PaymentIcon,
  NetworkIcon,
  ChatSNIcon,
  GinoIcon,
  SettingsIcon,
} from "../../../../assets/icons/MUI/LayoutIcons";
import APP_ROUTES from "../../../../routers/routeConstants";
import { useProfileData } from "../../../../hooks/useProfileData";
import { getEntityProfileRoute } from "../../../../routers/routeUtils";

import MobileSideNav from "./MobileSideNav";
import SideNavSkeleton from "./SideNavSkeleton";
import { sideNavStyles } from "./SideNav.styles";
import { RoutingMenuList } from "./RoutingMenuList";
import AiAnimatedIcon from "./AiAnimatedIcon";

const SideNav = ({ mobileOpen, handleCloseMobileNav }: { mobileOpen: boolean; handleCloseMobileNav: () => void }) => {
  const { t } = useTranslation();

  const navigationList = [
    {
      label: "gino",
      icon: <GinoIcon />,
      endIcon: <AiAnimatedIcon />,
      to: APP_ROUTES.GINO,
    },
    {
      label: "dashboard",
      icon: <DashboardIcon />,
      to: APP_ROUTES.DASHBOARD,
    },
    {
      label: "feed",
      icon: <FeedIcon />,
      to: `${APP_ROUTES.FEED}?type=BASIC&type=EVENT`,
    },
    {
      label: "goHire",
      icon: <GoServiceIcon />,
      to: APP_ROUTES.GO_HIRE,
      children: [
        {
          label: "jobs",
          to: APP_ROUTES.JOBS,
        },
        {
          label: "overviewServices",
          to: APP_ROUTES.OVERVIEW_SERVICES,
        },
        {
          label: "myRequests",
          to: APP_ROUTES.MY_SERVICES,
        },
      ],
    },
    {
      label: "explore",
      icon: <ExploreIcon />,
      to: APP_ROUTES.EXPLORE,
      children: [
        {
          label: "events",
          to: APP_ROUTES.EVENTS,
          isChild: true,
        },
        {
          label: "goStar",
          to: APP_ROUTES.GO_STAR,
          isChild: true,
        },
      ],
    },
    {
      label: "payment",
      icon: <PaymentIcon />,
      to: APP_ROUTES.PAYMENT,
    },

    {
      label: "network",
      icon: <NetworkIcon />,
      to: APP_ROUTES.NETWORK,
      children: [
        {
          label: "followers",
          to: APP_ROUTES.NETWORK,
          isChild: true,
        },
        {
          label: "following",
          to: APP_ROUTES.NETWORK,
          isChild: true,
        },
      ],
    },

    {
      label: "chat",
      icon: <ChatSNIcon />,
      to: APP_ROUTES.CHAT,
    },

    {
      label: "settings",
      icon: <SettingsIcon />,
      to: APP_ROUTES.PLANS_OVERVIEW,
      children: [
        {
          label: "plans",
          to: APP_ROUTES.PLANS_OVERVIEW,
          isChild: true,
        },
        {
          label: "team",
          isChild: true,
          disabled: true,
        },
        {
          label: "managedFootballEntities",
          isChild: true,
          disabled: true,
        },
      ],
    },
  ];
  return (
    <>
      {/* Inline sidenav on lg+ */}
      <Box sx={{ display: { xs: "none", lg: "block" } }}>
        <Box component="aside" sx={sideNavStyles.wrapper}>
          <Box sx={sideNavStyles.container}>
            <PersonalInfo />
            <Divider sx={sideNavStyles.divider} />
            <RoutingMenuList />
            {/* <MenuList /> */}
          </Box>
          <Box sx={sideNavStyles.mobileAppsBox}>
            <Typography sx={sideNavStyles.mobileAppsTitle}>{t("layout.sideNav.oneApp")}</Typography>
            <Typography sx={sideNavStyles.mobileAppsDescription}>{t("layout.sideNav.connectWith")}</Typography>
            <Box sx={sideNavStyles.mobileAppsLinksContainer}>
              <Link
                sx={sideNavStyles.storeLink}
                href="https://apps.apple.com/eg/app/goalngo/id6443919098"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppleIcon />
              </Link>
              <Link
                sx={sideNavStyles.storeLink}
                href="https://play.google.com/store/apps/details?id=com.goalngo_tech.goalngo&hl=en&pli=1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AndroidIcon />
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Mobile Drawer */}
      <MobileSideNav
        mobileOpen={mobileOpen}
        handleCloseMobileNav={handleCloseMobileNav}
        navigationList={navigationList}
      />
    </>
  );
};

export default SideNav;

const PersonalInfo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: userData, isLoading } = useProfileData();
  const { fullName, firstTwoChr, about, media, isVerified, entityId } = userData;

  if (isLoading) {
    return <SideNavSkeleton />;
  }

  return (
    <>
      <Box sx={sideNavStyles.avatarWrapper}>
        <Avatar sx={sideNavStyles.avatar} src={media}>
          {firstTwoChr}
        </Avatar>
        {isVerified && <VerifiedIcon sx={sideNavStyles.verifiedIcon} />}
      </Box>
      <Typography sx={sideNavStyles.fullName}>{fullName}</Typography>

      <Typography sx={sideNavStyles.about}>{about}</Typography>

      <Button variant="outlined-sm" onClick={() => navigate(getEntityProfileRoute(entityId!))} fullWidth>
        {t("layout.sideNav.viewProfile")}
      </Button>
    </>
  );
};
